import React, {useCallback, useMemo, useState} from 'react';
import {Alert, Box, Stack} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Loader from '../../layout/loading/loader';
import PhotoLightbox from '../../layout/PhotoLightbox.js';
import VerdictBanner from './VerdictBanner.js';
import ContextRail from './ContextRail.js';
import {ChecklistBlock, BooleenBlock} from './blocs/ActeBlocks.js';
import {LavageBlock, LavageImpossibleBlock, LavagePartielBlock, MaintenanceSignaleeBlock} from './blocs/LavageBlocks.js';
import {useGetInterventionDetailQuery} from '../../../../modules/interventions/interventionDetail.api.js';
import {BLOC, buildBlocs, buildVerdict, collectPhotos} from './interventionRules.js';
import {PhotoTileSettingsContext} from './primitives.js';
import {isClient} from '../../../../utils/roles.js';
import Constants from '../../../../utils/constants';

/** Carrousel des tuiles photo : mêmes défauts que le PhotoCarousel legacy (autoplay 4 s, pause au survol). */
const PHOTO_TILE_SETTINGS = {autoPlay: true, interval: 4000, duration: 350};

const BLOC_COMPONENTS = {
    [BLOC.LAVAGE]: LavageBlock,
    [BLOC.LAVAGE_IMPOSSIBLE]: LavageImpossibleBlock,
    [BLOC.LAVAGE_PARTIEL]: LavagePartielBlock,
    [BLOC.CHECKLIST]: ChecklistBlock,
    [BLOC.BOOLEEN]: BooleenBlock,
    [BLOC.MAINTENANCE_SIGNALEE]: MaintenanceSignaleeBlock,
};

/**
 * Vue intervention v2 (INT-02) — floorplan Object page, mobilité Mixte.
 * Verdict (1 s) → actes dans l'ordre du formulaire (10 s) → contexte (rail droit).
 * Données : `GET /interventions/{id}/detail` (RTK Query). Règles d'affichage : interventionRules.js.
 * Coexiste avec `InterventionView` (v1) derrière le toggle de `InterventionsPage`.
 */
function InterventionViewV2({interventionId, onPDFClick, photoSettings = PHOTO_TILE_SETTINGS}) {
    const {data: detail, isLoading, isError, error} = useGetInterventionDetailQuery(interventionId, {skip: !interventionId});
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const client = isClient();

    const verdict = useMemo(() => (detail ? buildVerdict(detail) : null), [detail]);
    const blocs = useMemo(() => buildBlocs(detail), [detail]);
    const photos = useMemo(() => collectPhotos(detail), [detail]);

    const openPhoto = useCallback((src) => {
        const idx = photos.findIndex((p) => p.src === src);
        setLightboxIndex(idx >= 0 ? idx : null);
    }, [photos]);

    if (isLoading) return <Loader/>;
    if (isError) {
        const status = error?.status;
        return (
            <Alert severity="error">
                {status === 403 ? "Cette intervention n'est pas dans votre périmètre." : "Impossible de charger l'intervention."}
            </Alert>
        );
    }
    if (!detail || !verdict) return null;

    return (
        <PhotoTileSettingsContext.Provider value={photoSettings}>
        <Box sx={{bgcolor: verdict.pageBackground, borderRadius: '20px', p: {xs: 1.5, md: 2.5}}}>
            <Stack spacing={2.5}>
                <VerdictBanner
                    verdict={verdict}
                    passage={detail.passage}
                    isClient={client}
                    onPDFClick={() => onPDFClick?.(detail.id, detail.passage?.dateEvenement)}
                />

                <Grid container spacing={2.5}>
                    <Grid size={{xs: 12, lg: 'grow'}} sx={{minWidth: 0}}>
                        <Stack spacing={2}>
                            {blocs.map((bloc, i) => {
                                const Component = BLOC_COMPONENTS[bloc.type];
                                return Component ? (
                                    <Component key={`${bloc.type}-${i}`} numero={i + 1} bloc={bloc}
                                               assetsUrl={Constants.API_ASSETS_PHOTO_INTERVENTION} onOpen={openPhoto}/>
                                ) : null;
                            })}
                        </Stack>
                    </Grid>
                    <Grid size={{xs: 12, lg: 'auto'}} sx={{width: {lg: 340}}}>
                        <ContextRail detail={detail} isClient={client}/>
                    </Grid>
                </Grid>
            </Stack>

            <PhotoLightbox
                photos={photos}
                index={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
                onIndexChange={setLightboxIndex}
                assetsUrl={Constants.API_ASSETS_PHOTO_INTERVENTION}
            />
        </Box>
        </PhotoTileSettingsContext.Provider>
    );
}

export default InterventionViewV2;
